import { initializeApp } from "firebase/app";
import { pricePeriods } from "./utils/pricePeriods";
import calculatePeopleSupplement from "./utils/hooks/calculateSupp";
import calculateTax, {
  calculateTaxFormatted,
} from "./utils/hooks/calculateTax";
import { DateTime } from "luxon";
import {
  getFirestore,
  where,
  collection,
  query,
  deleteDoc,
  updateDoc,
  doc,
  getDocs,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { getStorage } from "firebase/storage";
import "firebase/compat/storage";

const firebaseConfig = {
  apiKey: "AIzaSyBSBLquPuMVOV8d5Aiu1i87MtBbyavrIjw",
  authDomain: "residenza-storica.firebaseapp.com",
  projectId: "residenza-storica",
  storageBucket: "residenza-storica.appspot.com",
  messagingSenderId: "178576986784",
  appId: "1:178576986784:web:56b54e5aaff9ebf5452fc5",
  measurementId: "G-1Y6QT5E97M",
};
// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries
const app = initializeApp(firebaseConfig);
export const storage = getStorage(app);
export const db = getFirestore(app);

export const fetchBookingsFromFirebase = async () => {
  const db = getFirestore(); // Initialize Firestore
  const bookingsCollection = collection(db, "bookings"); // Specify your collection
  const snapshot = await getDocs(bookingsCollection);

  const bookings = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  return bookings; // Returns an array of bookings
};

export async function getAvailability(roomId) {
  const db = getFirestore();
  const availabilityRef = collection(db, "availability"); // Nome della collection nel DB Firebase
  const q = query(availabilityRef, where("roomId", "==", roomId)); // Filtra per roomId
  const querySnapshot = await getDocs(q);

  const availabilityData = [];
  querySnapshot.forEach((doc) => {
    availabilityData.push({ id: doc.id, ...doc.data() });
  });

  return availabilityData; // Ritorna i dati della disponibilità
}

export async function deleteOldAvailabilities() {
  const db = getFirestore();
  const today = new Date();
  const threeDaysAgo = new Date(today);
  threeDaysAgo.setDate(today.getDate() - 3); // Calcola la data di 3 giorni fa

  try {
    const availabilityCollection = collection(db, "availability");
    const availabilitySnapshot = await getDocs(availabilityCollection);

    // Filtra i documenti da eliminare
    const deletePromises = availabilitySnapshot.docs
      .filter((availabilityDoc) => {
        const availabilityData = availabilityDoc.data();
        const endDate = availabilityData.endDate.toDate(); // Converti il timestamp in una data
        return endDate < threeDaysAgo; // Se l'endDate è più vecchia di 3 giorni
      })
      .map((availabilityDoc) => {
        console.log(`Eliminazione della disponibilità: ${availabilityDoc.id}`);
        return deleteDoc(doc(db, "availability", availabilityDoc.id));
      });

    // Esegui tutte le promesse di cancellazione
    await Promise.all(deletePromises);
    console.log(
      "Le disponibilità con endDate più vecchie di 3 giorni sono state eliminate con successo."
    );
  } catch (error) {
    console.error("Errore durante l'eliminazione delle disponibilità:", error);
  }
}

export async function updateAvailability(
  roomId,
  startDate,
  endDate,
  adultsCount,
  childrenCount,
  phoneNumber, // Aggiunto parametro
  customerName, // Aggiunto parametro
  email,
  price,
  totalPrice, // Aggiunto parametro totalPrice
  isManual,
  isWebsite,
  cashInfo = {},
  paymentMethodUsed = "card",
  lang = "it" // Nuovo parametro per la lingua
) {
  const start = DateTime.fromISO(startDate, { zone: "Europe/Rome" })
    .set({ hour: 14, minute: 0, second: 0, millisecond: 0 })
    .toJSDate();
  const end = DateTime.fromISO(endDate, { zone: "Europe/Rome" })
    .set({ hour: 10, minute: 59, second: 0, millisecond: 0 })
    .toJSDate();

  const bookingNumber = `1811-RS-${Math.floor(Math.random() * 10000)}`;
  const key = `${roomId}_${start.toISOString().split("T")[0]}_${
    end.toISOString().split("T")[0]
  }`;

  // Funzione per inviare email di conferma
  const sendBookingConfirmationEmail = async (bookingData) => {
    if (!email || email === "No email" || email === "N/A") {
      console.log("Email non valida, invio email saltato");
      return;
    }

    try {
      // Calcola la tassa di soggiorno

      const tax = calculateTaxFormatted(adultsCount, startDate, endDate);

      // Prepara i dati per l'email
      const emailData = {
        email: email,
        bookingDetails: {
          name: customerName,
          phone: phoneNumber,
          dates: {
            startDate: start.toISOString(),
            endDate: end.toISOString(),
          },
          rooms: [roomId], // Array con il roomId
          email: email,
          paymentMethodUsed: paymentMethodUsed,
          adultCount: adultsCount,
          childrenCount: childrenCount,
          totalPrice: totalPrice ? `${totalPrice}€` : "0€",
          tax: tax,
          lang: lang,
        },
      };

      // Invia l'email tramite l'API
      const response = await fetch(
        "https://residenzastorica1811.vercel.app/send-confirmation-email",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(emailData),
        }
      );

      if (response.ok) {
        console.log(
          "Email di conferma inviata con successo per la prenotazione:",
          bookingNumber
        );
      } else {
        console.error(
          "Errore nell'invio dell'email di conferma:",
          response.status
        );
      }
    } catch (error) {
      console.error("Errore durante l'invio dell'email di conferma:", error);
    }
  };

  try {
    const availabilityRef = doc(db, "availability", key);
    const availabilityDoc = await getDoc(availabilityRef);

    let isNewBooking = false;

    if (!availabilityDoc.exists()) {
      console.log("Il documento non esiste. Creazione di un nuovo documento.");
      isNewBooking = true;
      // Se il documento non esiste, creane uno nuovo
      await setDoc(availabilityRef, {
        roomId: roomId,
        startDate: start,
        endDate: end,
        adultsCount: adultsCount,
        childrenCount: childrenCount,
        phoneNumber: phoneNumber, // Salviamo il numero di telefono
        customerName: customerName, // Salviamo il nome del cliente
        email: email,
        price: price,
        totalPrice: totalPrice,
        isManual: isManual,
        isWebsite: isWebsite,
        cashInfo: cashInfo,
        paymentMethodUsed: paymentMethodUsed,
        bookingNumber: bookingNumber,
      });
    } else {
      console.log(
        "Il documento esiste. Aggiornamento del documento esistente."
      );
      // Se il documento esiste, aggiornalo
      await updateDoc(availabilityRef, {
        adultsCount: adultsCount,
        childrenCount: childrenCount,
        phoneNumber: phoneNumber, // Salviamo il numero di telefono
        customerName: customerName, // Salviamo il nome del cliente
        email: email,
        price: price,
        totalPrice: totalPrice,
        isManual: isManual,
        isWebsite: isWebsite,
        cashInfo: cashInfo,
        paymentMethodUsed: paymentMethodUsed,
        bookingNumber: bookingNumber,
      });
    }

    // Aggiorna il documento della stanza per impostare `isAvailable` a `false`
    const roomRef = doc(db, "rooms", roomId);
    const roomDoc = await getDoc(roomRef);
    if (roomDoc.exists()) {
      await updateDoc(roomRef, { isAvailable: false });
    } else {
      console.error(`Documento della stanza con ID ${roomId} non esiste.`);
    }

    // Invia email SOLO per prenotazioni dal sito web (non da admin o Booking.com)
    if (isWebsite && isNewBooking) {
      await sendBookingConfirmationEmail({
        roomId,
        startDate,
        endDate,
        adultsCount,
        childrenCount,
        phoneNumber,
        customerName,
        email,
        price,
        totalPrice,
        paymentMethodUsed,
        cashInfo,
      });
    }
  } catch (error) {
    console.error("Errore durante l'aggiornamento della disponibilità:", error);
  }
}

export async function getDailyPrice(roomId, dateString) {
  const date = new Date(dateString);
  const dailyPricesCollection = collection(db, "dailyPrices");

  try {
    const dailyPriceQuery = query(
      dailyPricesCollection,
      where("roomId", "==", roomId),
      where("startDate", "<=", date),
      where("endDate", ">=", date)
    );
    const dailyPriceSnapshot = await getDocs(dailyPriceQuery);
    if (dailyPriceSnapshot) {
      let applicablePrice = 0;
      let minNights = 0;
      dailyPriceSnapshot.docs.forEach((doc) => {
        applicablePrice = doc.data().price;
        minNights = doc.data().minNights;
      });

      return { applicablePrice: applicablePrice, minNights: minNights };
    } else {
      return 0;
    }
  } catch (error) {
    console.error("Errore durante la ricerca dei prezzi giornalieri:", error);
    return 0;
  }
}
export async function fetchAllDailyPrices() {
  const dailyPricesCollection = collection(db, "dailyPrices");
  const dailyPriceSnapshot = await getDocs(dailyPricesCollection);
  const prices = dailyPriceSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
  return prices;
}
export async function fetchDailyPrices(roomId) {
  const dailyPricesCollection = collection(db, "dailyPrices");
  const dailyPriceQuery = query(
    dailyPricesCollection,
    where("roomId", "==", roomId)
  );
  const dailyPriceSnapshot = await getDocs(dailyPriceQuery);
  const prices = dailyPriceSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
  return prices;
}

export async function fetchMinNights() {
  // Riferimento alla collection che contiene i minimi di notti
  const minNightsCollection = collection(db, "minNightsSettings");
  const minNightsSnapshot = await getDocs(minNightsCollection);

  // Mappiamo i dati dei documenti in un array con ID e dati
  const minNights = minNightsSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  return minNights;
}

export async function deletePrice(roomId, startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Genera la chiave del documento da eliminare
  const key = `${roomId}_${start.toISOString().split("T")[0]}_${
    end.toISOString().split("T")[0]
  }`;

  try {
    // Riferimento al documento dei prezzi giornalieri
    const priceDocRef = doc(db, "dailyPrices", key);

    // Controlla se il documento esiste
    const docSnap = await getDoc(priceDocRef);
    if (docSnap.exists()) {
      // Elimina il documento
      await deleteDoc(priceDocRef);
      console.log(`Prezzo per ${key} eliminato con successo.`);
      alert(`Prezzo per ${key} eliminato con successo.`);
    } else {
      console.error(`Documento ${key} non trovato. Impossibile eliminarlo.`);
      alert(`Documento ${key} non trovato. Impossibile eliminarlo.`);
    }
  } catch (error) {
    console.error(
      "Errore durante l'operazione di cancellazione del prezzo:",
      error
    );
    alert("Errore durante l'operazione di cancellazione del prezzo.");
  }
}
export const getAllDocumentsFromMinNightsSettings = async (start) => {
  try {
    const minNightsSettingsCollection = collection(db, "minNightsSettings");
    const minNightsSettingsSnapshot = await getDocs(
      minNightsSettingsCollection
    );
    const startDateFilter = new Date(start);
    const minNightsSettings = {};
    minNightsSettingsSnapshot.forEach((doc) => {
      const data = doc.data();
      const roomId = data.roomId;
      const startMinNight = data.startDate.toDate();
      const endMinNight = data.endDate.toDate();

      // Converte le date in formato "YYYY-MM-DD" per confrontare solo il giorno
      const startMinNightStr = startMinNight.toISOString().split("T")[0];
      const startDateFilterStr = startDateFilter.toISOString().split("T")[0];

      if (
        startMinNightStr === startDateFilterStr &&
        startDateFilter <= endMinNight
      ) {
        // Priorità ai documenti che iniziano esattamente nel giorno cercato
        if (!minNightsSettings[roomId]) {
          minNightsSettings[roomId] = [];
        }
        minNightsSettings[roomId].push({
          minNights: data.minNights,
          start: startMinNight,
          end: endMinNight,
          numberOfNights: Math.ceil(
            (endMinNight - startMinNight) / (1000 * 60 * 60 * 24)
          ),
        });
      } else if (
        startMinNight <= startDateFilter &&
        startDateFilter <= endMinNight
      ) {
        // Se non c'è un match esatto, prendiamo comunque un documento valido
        if (!minNightsSettings[roomId]) {
          minNightsSettings[roomId] = [];
        }

        let updatedMinNights = data.minNights;

        // Calcola i giorni trascorsi
        const daysElapsed = Math.ceil(
          (startDateFilter - startMinNight) / (1000 * 60 * 60 * 24)
        );

        // Aggiorna minNights scalando i giorni trascorsi, ma minimo 1 notte
        updatedMinNights = Math.max(data.minNights - daysElapsed, 1);

        // Se start coincide con endDate, imposta minNights a 0

        minNightsSettings[roomId].push({
          minNights: updatedMinNights,
          start: startMinNight, // Mantiene invariato
          end: endMinNight, // Mantiene invariato
          numberOfNights: Math.ceil(
            (endMinNight - startMinNight) / (1000 * 60 * 60 * 24)
          ),
        });
      }
    });

    // Se ci sono più documenti per una stanza, prendiamo solo il più recente
    Object.keys(minNightsSettings).forEach((roomId) => {
      minNightsSettings[roomId].sort((a, b) => b.start - a.start);
      minNightsSettings[roomId] = minNightsSettings[roomId][0];
    });
    return minNightsSettings;
  } catch (error) {
    console.error("Errore durante il recupero dei documenti:", error);
  }
};

export async function checkAvailability(
  startDate,
  endDate,
  childrenCount,
  adultCount
) {
  try {
    // CORREZIONE: Gestione robusta dei fusi orari con validazione
    let start, end;

    try {
      start = DateTime.fromISO(startDate, { zone: "Europe/Rome" })
        .set({ hour: 14, minute: 0, second: 0, millisecond: 0 })
        .toJSDate();
    } catch (error) {
      console.error("Errore nella conversione startDate:", error);
      // Fallback: usa Date standard con validazione
      start = new Date(startDate);
      if (isNaN(start.getTime())) {
        throw new Error("Data di inizio non valida");
      }
      start.setHours(14, 0, 0, 0);
    }

    try {
      end = DateTime.fromISO(endDate, { zone: "Europe/Rome" })
        .set({ hour: 10, minute: 59, second: 0, millisecond: 0 })
        .toJSDate();
    } catch (error) {
      console.error("Errore nella conversione endDate:", error);
      // Fallback: usa Date standard con validazione
      end = new Date(endDate);
      if (isNaN(end.getTime())) {
        throw new Error("Data di fine non valida");
      }
      end.setHours(10, 59, 0, 0);
    }

    // Validazione finale delle date
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      throw new Error("Date non valide dopo la conversione");
    }

    if (start >= end) {
      throw new Error(
        "La data di inizio deve essere precedente alla data di fine"
      );
    }

    // Calcola la differenza in millisecondi e poi il numero di notti
    const differenceInMillis = end - start;
    const nights = Math.ceil(differenceInMillis / (1000 * 60 * 60 * 24));
    // Prepara la query per verificare la disponibilità
    const availabilityCollection = collection(db, "availability");

    // CORREZIONE: Query che ignora gli orari e usa solo le date
    // Convertiamo le date in stringhe YYYY-MM-DD per il confronto
    const startDateStr = start.toISOString().split("T")[0];
    const endDateStr = end.toISOString().split("T")[0];

    // Query semplificata che prende tutti i documenti e poi filtra per sovrapposizione
    const availabilityQuery = query(availabilityCollection);

    const availabilitySnapshot = await getDocs(availabilityQuery);
    const unavailableRooms = new Set();
    const roomsID = ["room1", "room2", "room3", "room4", "room5"];

    // CORREZIONE: Logica di sovrapposizione che mantiene gli orari corretti

    availabilitySnapshot.forEach((doc) => {
      const roomId = doc.data().roomId;
      const docData = doc.data();

      // Controlla sovrapposizione mantenendo gli orari standard
      let isOverlapping = false;

      if (docData.startDate && docData.endDate) {
        let docStart, docEnd;

        // Converti i timestamp in date
        if (docData.startDate.toDate) {
          docStart = docData.startDate.toDate();
          docEnd = docData.endDate.toDate();
        } else {
          docStart = new Date(docData.startDate);
          docEnd = new Date(docData.endDate);
        }

        // CORREZIONE: Assicurati che gli orari siano sempre standard
        // startDate deve essere alle 14:00 (2 del pomeriggio)
        docStart.setHours(14, 0, 0, 0);
        // endDate deve essere alle 10:59 del mattino
        docEnd.setHours(10, 59, 0, 0);

        // CORREZIONE: Formula di sovrapposizione corretta
        // Due prenotazioni si sovrappongono se:
        // - la fine della nuova (end) è DOPO l'inizio della vecchia (docStart) E
        // - l'inizio della nuova (start) è PRIMA della fine della vecchia (docEnd)
        isOverlapping = end > docStart && start < docEnd;
      }

      if (isOverlapping) {
        unavailableRooms.add(roomId);
      }
    });

    // Recupera la configurazione dei minimi notti per ogni stanza dalla collezione "minNightsSettings"
    const minNightsSettingsCollection = collection(db, "minNightsSettings");
    const minNightsSettingsSnapshot = await getDocs(
      minNightsSettingsCollection
    );
    const minNightsSettings = {};

    minNightsSettingsSnapshot.forEach((doc) => {
      const data = doc.data();
      const roomId = data.roomId;
      const minNights = data.minNights;
      // Converti i Timestamp in oggetti Date
      const startMinNight = data.startDate.toDate();
      const endMinNight = data.endDate.toDate();
      // Calcola il numero di notti (arrotonda sempre per eccesso)
      const numberOfNights = Math.ceil(
        (endMinNight - startMinNight) / (1000 * 60 * 60 * 24)
      );
      // Crea un oggetto per ogni roomId con il formato desiderato
      minNightsSettings[roomId] = {
        minNights,
        start: startMinNight.toString(), // Formato "Mon Apr 28 2025 00:00:00 GMT+0200 (Central European Summer Time)"
        end: endMinNight.toString(), // Formato "Mon Apr 28 2025 00:00:00 GMT+0200 (Central European Summer Time)"
        numberOfNights,
      };
    });

    const formattedDate = start.toISOString().split("T")[0]; // Ottieni solo la data, non l'orario
    const newSetting = await getAllDocumentsFromMinNightsSettings(
      formattedDate
    );
    const roomsArray = {}; // Usa un oggetto invece di un array

    roomsID.forEach((roomId) => {
      if (!newSetting[roomId]) {
        // Se la stanza non esiste, creala con valori predefiniti
        const startDate = new Date(start); // Crea la data di partenza
        const endDate = new Date(startDate); // Copia la data di partenza per la fine

        // Aggiungi 5 giorni alla data di partenza, ma impostando l'ora
        newSetting[roomId] = {
          minNights: 0,
          start: startDate.toString(), // Data di partenza
          end: endDate.toString(), // Data di fine
          numberOfNights: 0,
        };
      }

      // Aggiungi ogni stanza all'oggetto, utilizzando roomId come chiave
      roomsArray[roomId] = {
        roomId: roomId,
        ...newSetting[roomId], // Aggiungi le informazioni della stanza
      };
    });

    // Filtra le camere che soddisfano i minimi notti e non sono nelle camere non disponibili
    const availableRooms = roomsID.filter((roomId) => {
      const roomSettings = roomsArray[roomId];
      const isAvailable = !unavailableRooms.has(roomId);
      const meetsMinNights = roomSettings.minNights <= nights; // Verifica che il numero di notti richieste sia valido
      return isAvailable && meetsMinNights;
    });
    // Crea un array di promesse per calcolare i prezzi delle camere disponibili
    const pricePromises = availableRooms.map(async (roomId) => {
      const roomPrices = [];
      let totalPrice = 0;
      let currentDate = new Date(start);
      // Esegui il calcolo dei prezzi per tutte le date in parallelo
      while (currentDate < end) {
        const currentDateString = currentDate.toISOString().split("T")[0];
        const { applicablePrice } = await getDailyPrice(roomId, currentDate);
        if (applicablePrice === 0) {
          const currentMonth = currentDate.getMonth();
          const pricePeriod = pricePeriods[roomId].find(
            (period) =>
              currentMonth >= period.start && currentMonth <= period.end
          );
          const price = pricePeriod ? pricePeriod.price : 0;
          totalPrice += price;
        } else {
          totalPrice += applicablePrice;
        }

        roomPrices.push({
          date: currentDateString,
          price: totalPrice,
        });

        // Incrementa la data di un giorno
        currentDate.setDate(currentDate.getDate() + 1);
      }

      const { totalSupplement } = calculatePeopleSupplement(
        adultCount,
        childrenCount,
        roomId,
        nights
      );

      return {
        roomId,
        prices: roomPrices,
        totalPrice: totalPrice,
        totalSupplement: totalSupplement,
      };
    });
    const roomsWithPrices = (await Promise.all(pricePromises)).filter(
      (room) => room !== null
    );

    return roomsWithPrices;
  } catch (error) {
    console.error("Errore in checkAvailability:", error);
    throw error;
  }
}

export async function addPriceUpdate(roomId, startDate, endDate, newPrice) {
  const start = new Date(startDate);
  const end = new Date(endDate);

  start.setHours(14, 0, 0, 0); // 14:00:00
  end.setHours(10, 59, 0, 0); // 10:59:00

  const dailyPricesCollection = collection(db, "dailyPrices");
  const q = query(
    dailyPricesCollection,
    where("roomId", "==", roomId),
    where("startDate", "<=", end),
    where("endDate", ">=", start)
  );
  const querySnapshot = await getDocs(q);
  if (!querySnapshot.empty) {
    console.error(
      "Esistono già documenti per queste date. Prezzo non aggiornato."
    );
    alert("Esistono già documenti per queste date. Prezzo non aggiornato.");
    return;
  }

  const numericPrice = Number(newPrice);

  await setDoc(
    doc(
      db,
      "dailyPrices",
      `${roomId}_${start.toISOString().split("T")[0]}_${
        end.toISOString().split("T")[0]
      }`
    ),
    {
      roomId: roomId,
      price: numericPrice,
      startDate: start,
      endDate: end,
    }
  );

  alert("Prezzo aggiornato con successo", roomId, startDate, endDate, newPrice);
}

export async function addMinNightsSetting(
  roomIds,
  startDate,
  endDate,
  minNights
) {
  const start = new Date(startDate);
  const end = new Date(endDate);

  start.setHours(14, 0, 0, 0); // 14:00:00
  end.setHours(10, 59, 0, 0); // 10:59:00

  try {
    // Creiamo un array di promesse per aggiornare tutte le camere
    const updates = roomIds.map(async (roomId) => {
      const docRef = doc(
        db,
        "minNightsSettings",
        `${roomId}_${start.toISOString().split("T")[0]}_${
          end.toISOString().split("T")[0]
        }`
      );

      await setDoc(docRef, {
        roomId: roomId,
        minNights: minNights,
        startDate: start,
        endDate: end,
      });
    });

    // Attendere il completamento di tutte le operazioni
    await Promise.all(updates);

    alert(
      `Minimo notti aggiornato con successo per le camere: ${roomIds.join(
        ", "
      )}`
    );
  } catch (error) {
    console.error("Errore durante l'aggiornamento delle notti minime:", error);
    alert("Si è verificato un errore durante l'aggiornamento.");
  }
}

export async function deleteBooking(roomId, startDate, endDate, isPrice) {
  const start = new Date(startDate);
  const end = new Date(endDate);

  start.setHours(14, 0, 0, 0); // 14:00:00
  end.setHours(10, 59, 0, 0); // 10:59:00
  let collectionName = isPrice ? "dailyPrices" : "availability";
  const key = `${roomId}_${start.toISOString().split("T")[0]}_${
    end.toISOString().split("T")[0]
  }`;
  try {
    const availabilityDocRef = doc(db, collectionName, key);
    const docSnap = await getDoc(availabilityDocRef);
    if (docSnap.exists()) {
      await deleteDoc(availabilityDocRef);
      console.log(`Documento ${key} eliminato con successo.`);
    } else {
      console.error(`Documento ${key} non trovato. Impossibile eliminarlo.`);
    }
  } catch (error) {
    console.error("Errore durante l'operazione di cancellazione:", error);
  }
}

export async function deleteOldBookings() {
  const today = new Date();
  const twentyDaysAgo = new Date(today);
  twentyDaysAgo.setDate(today.getDate() - 20); // Calcola la data di 20 giorni fa

  try {
    const bookingsCollection = collection(db, "bookings");
    const bookingsSnapshot = await getDocs(bookingsCollection);

    // Filtra i documenti da eliminare
    const deletePromises = bookingsSnapshot.docs
      .filter((bookingDoc) => {
        const bookingData = bookingDoc.data();
        const bookingDate = new Date(bookingData.startDate.seconds * 1000); // Converti il timestamp in data
        return bookingDate < twentyDaysAgo; // Se il booking è più vecchio di 20 giorni
      })
      .map((bookingDoc) => {
        console.log(`Eliminazione del booking: ${bookingDoc.id}`);
        return deleteDoc(doc(db, "bookings", bookingDoc.id));
      });

    // Esegui tutte le promesse di cancellazione
    await Promise.all(deletePromises);
    console.log(
      "I booking più vecchi di 20 giorni sono stati eliminati con successo."
    );
  } catch (error) {
    console.error("Errore durante l'eliminazione dei booking:", error);
  }
}
export async function updateBooking(
  bookingId,
  newStartDate,
  newEndDate,
  newAdultCount,
  newChildrenCount,
  phoneNumber,
  customerName,
  email,
  price,
  bookingNumber
) {
  const start = new Date(newStartDate);
  const end = new Date(newEndDate);
  start.setHours(14, 0, 0, 0); // 14:00:00
  end.setHours(10, 59, 0, 0); // 10:59:00

  try {
    // Trova il documento con lo stesso bookingNumber
    const bookingQuery = query(
      collection(db, "availability"),
      where("bookingNumber", "==", bookingNumber)
    );

    const querySnapshot = await getDocs(bookingQuery);

    if (!querySnapshot.empty) {
      // Assumiamo che ci sia solo un documento che corrisponde al bookingNumber
      const bookingDoc = querySnapshot.docs[0];
      console.log("Prenotazione trovata. Aggiornamento in corso...");
      const existingData = bookingDoc.data();
      console.log("Dati esistenti (prima dell'aggiornamento):", existingData);

      // Crea un oggetto per i cambiamenti
      const updates = {
        startDate: start,
        endDate: end,
        adultsCount: newAdultCount || existingData.adultsCount,
        childrenCount: newChildrenCount || existingData.childrenCount,
        phoneNumber: phoneNumber || existingData.phoneNumber,
        customerName: customerName || existingData.customerName,
        email: email || existingData.email,
        price: price || existingData.price,
      };

      // Aggiorna il documento
      await updateDoc(bookingDoc.ref, updates);
      console.log("Prenotazione aggiornata con successo!");

      // Verifica l'aggiornamento
      const updatedBookingDoc = await getDoc(bookingDoc.ref);
      console.log(
        "Dati aggiornati (dopo l'aggiornamento):",
        updatedBookingDoc.data()
      );
    } else {
      console.error("Prenotazione non trovata. Impossibile aggiornare.");
    }
  } catch (error) {
    console.error("Errore durante l'aggiornamento della disponibilità:", error);
  }
}

// Funzione per aggiornare il prezzo di un giorno esistente
export const updateDailyPrice = async (priceId, newPrice) => {
  const priceRef = doc(db, "dailyPrices", priceId);

  try {
    await updateDoc(priceRef, { price: parseFloat(newPrice) });
    console.log("Prezzo aggiornato con successo!");
  } catch (error) {
    console.error("Errore durante l'aggiornamento del prezzo:", error);
    throw error; // Rilancia l'errore per la gestione nel frontend
  }
};
